import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FaQuestion from 'react-icons/lib/fa/question-circle-o';
import ReactTooltip from 'react-tooltip';
import { GetBrandedAppName, GetBrandedCompanyName, GetBrandedCurriculumName } from '../../services/BrandingService';
import { useTranslation } from 'react-i18next';

export const tooltipTypes = {
  scoreType: 'scoreType',
  scale: 'scale',
  benchmarks: 'benchmarks',
  metric: 'metric',
  curriculum: 'curriculum',
  score: 'score',
  pr: 'pr',
  readingLevel: 'readingLevel',
  area: 'area',
  phonologicalAwareness: 'phonologicalAwareness',
  sightRecognition: 'sightRecognition',
  vocabulary: 'vocabulary',
  decoding: 'decoding',
  resourcesAndExamples: 'resourcesAndExamples',
  appropriatelyChallengingSkills: 'appropriatelyChallengingSkills',
  riskLevel: 'riskLevel',
  atRisk: 'atRisk',
  screeningWindows: 'screeningWindows',
  progressKey: 'progressKey',
  masteryKey: 'masteryKey',
  customARMLevelMappingHeader: 'customARMLevelMappingHeader',
  benchmarkARMToCustomLevelExceedsMax: 'benchmarkARMToCustomLevelExceedsMax',
}

const getTooltipText = (type, t) => {
  const appName = GetBrandedAppName();
  const curriculumName = GetBrandedCurriculumName();
  switch (type) { 
    case (tooltipTypes.scoreType): 
      return t("tooltip_scoreType");
    case (tooltipTypes.scale):
      return t("tooltip_scale");
    case (tooltipTypes.benchmarks): 
      return t("tooltip_benchmarks");
    case (tooltipTypes.metric):  
      return t("tooltip_metric");
    case (tooltipTypes.curriculum):  
      return t("tooltip_curriculum", {curriculumName});
    case (tooltipTypes.score):   
      return 'This shows student\'s most recent assessment score and the date it was taken or rescored.';
    case (tooltipTypes.pr):   
      return t("tooltip_pr");
    case (tooltipTypes.readingLevel):   
      return t("tooltip_readingLevel");
    case (tooltipTypes.area):   
      return 'AREA stands for Amira Reading Estimated Age. <br />The value tells you how this student compares to others as an overall reader.   Essentially, the student is placed into a cohort of students nationally. The typical age of the students in that cohort is this student\'s AREA score.';
    case (tooltipTypes.phonologicalAwareness):   
      return `This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills reflect a scope and sequence for Letter-Sound Correspondence mastery. ${appName} is tracking about 180 specific letter-sounds`;
    case (tooltipTypes.sightRecognition):   
      return `This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills are specific high frequency words that students should ultimately read via sight recognition. ${appName} is tracking about 400 high frequency words, most from the Dolch list.`;
    case (tooltipTypes.vocabulary):   
      return 'This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills reflect the Into Reading scope and sequence for Vocabulary.';
    case (tooltipTypes.decoding):   
      return 'This tab shows a student\'s Percentile Ranking in relationship to all students nationally.  The score reflects a student\'s ability to segment and blend words, and read grade-level appropriate words.';
    case (tooltipTypes.resourcesAndExamples):   
      return 'We recommend these resources to address the gaps in the student\'s mastery, as identified above.  Each resource is selected to target a student\'s current abilities and growth areas.';
    case (tooltipTypes.appropriatelyChallengingSkills):   
      return 'These skills should be emphasized in individual and small group instruction.';
    case (tooltipTypes.riskLevel):   
      return t("tooltip_riskLevel");
    case (tooltipTypes.atRisk):   
      return t("tooltip_atRisk");
    case (tooltipTypes.screeningWindows):   
      return 'Within the date range specified, students who haven’t yet completed a benchmark assessment in the current screening window will be automatically presented with one the next time they log in.';
    case (tooltipTypes.progressKey):   
      return `The blue line on the chart shows how the student\'s mastery is changing. <br /><br />The triangle indicates the predicted mastery level by the end of the student year. <br /><br />This progress line reflects the improvement ${appName} is documenting in the student\'s reading.  The line can show significant growth, very gradual growth or even, rarely, a slide in the student\'s fluency.`
    case (tooltipTypes.masteryKey):   
      return `The current best estimate of the student's ARM Score, factoring in recent assessments and practice sessions.`
    case (tooltipTypes.customARMLevelMappingHeader):
      return `The placement for your curriculum based on the student's ARM score.`
    case (tooltipTypes.benchmarkARMToCustomLevelExceedsMax):
      return `Student's current reading mastery level suggests the student does not need SPIRE for foundational skill development.`
    default:
      return;
  }
}

export const Tooltip = (props) => {
  const {type, content, displayedText} = props;

  const {t, i18n} = useTranslation('reports');

  const id = useMemo(() => {
    return type + 'Tooltip';
  }, [type]); 
  const textId = useMemo(() => {
    return id + 'Text';
  }, [id]); 
  const text = useMemo(() => {
    if (displayedText || content) {
      return content;
    } else {
    return getTooltipText(type, t);
    }
  }, [displayedText, content, type, i18n.language]);

  return (
    <span className="amiraTooltipContainer" data-tip data-for={id} data-html={true}>
      <FaQuestion className="amiraTooltipIcon" size="18"/>
      <ReactTooltip id={id} className="amiraTooltip" place="top" effect="solid">
        {`<div id=${textId} class="amiraTooltipText">${text}</div>`}
      </ReactTooltip>
    </span>
  );
}

Tooltip.propTypes = {
  type: PropTypes.string,
  content: PropTypes.any,
}
